import BaseLayer from 'ol/layer/Base';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import MVT from 'ol/format/MVT';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import { Circle } from 'ol/style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { debounceTime, filter, merge } from 'rxjs';
import { GenDataCenterOperationStatus } from '../../../../generated/serverModels/GenDataCenterOperationStatus';
import { GenDataCenterProvider } from '../../../../generated/serverModels/GenDataCenterProvider';
import { AppService } from '../../../../services/app.service';
import { MapService } from '../../../../services/map.service';
import { BaseUILayer } from '../../../classes/layers/base-ui-layer';
import { DatacenterOperationalStatusPipe } from '../../../pipes/datacenter-operational-status.pipe';
import { DatacenterProviderPipe } from '../../../pipes/datacenter-provider.pipe';
import { LayerHeaderComponent } from '../../layer-header/layer-header.component';

@Component({
  selector: 'tgrid-datacenters-layer',
  imports: [
    FormsModule,
    LayerHeaderComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    DatacenterProviderPipe,
    DatacenterOperationalStatusPipe,
  ],
  templateUrl: './datacenters-layer.component.html',
  styleUrl: './datacenters-layer.component.scss',
})
export class DatacentersLayerComponent extends BaseUILayer<BaseLayer> {
  private appService = inject(AppService);

  protected providers = GenDataCenterProvider;
  protected statuses = GenDataCenterOperationStatus;


  protected filterGroup: FormGroup = new FormGroup({
    provider: new FormControl<GenDataCenterProvider | 'ALL'>('ALL'),
    operationalStatus: new FormControl<GenDataCenterOperationStatus | 'ALL'>('ALL'),
  });


  constructor() {
    super();

    merge(this.filterGroup.valueChanges, toObservable(this.appService.selectedStates))
      .pipe(
        filter(() => this.checked),
        debounceTime(250),
        takeUntilDestroyed(),
      ).subscribe(() => {
        const source = (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).getSource();
        source?.setUrl(this.buildUrl());
      });


  }


  private buildUrl(): string {
    const filters = this.filterGroup.getRawValue();
    const states = this.appService.selectedStates().map(s => s.abbreviation).join(',');

    let url = this.mapLayer().serviceUrl;
    url += `?state=${states}`;

    if (filters.operationalStatus !== 'ALL') {
      url += `&operational-status=${filters.operationalStatus}`;
    }
    if (filters.provider !== 'ALL') {
      url += `&provider-type=${filters.provider}`;
    }
    return url;
  }

  override toggleLayer(checked: boolean): void {
    this.checked = checked;
    if (this.checked) {
      if (this.mapLayer().renderedLayer) {
        const source = (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).getSource();
        source?.setUrl(this.buildUrl());
      } else {
        this.mapLayer().renderedLayer = new VectorTileLayer({
          declutter: true,
          renderBuffer: 200,
          properties: {
            type: this.mapLayer().serviceType,
            name: this.mapLayer().displayName,
            popoverFunction: this.mapLayer().createPopover.bind(this.mapLayer()),
          },
          source: new VectorTileSource({
            format: new MVT(),
            url: this.buildUrl(),
            attributions: this.mapLayer().buildAttribution(),
          }),
          style: (feature) => {
            const properties = feature.getProperties();
            const key: string = properties['operationalStatus'];

            let color = '#444';
            switch (key) {
              case GenDataCenterOperationStatus.OPERATIONAL.toString():
                color = 'rgb(94,182,92)';
                break;
              case GenDataCenterOperationStatus.UNDER_CONSTRUCTION.toString():
                color = 'rgb(228,155,39)';
                break;
              case GenDataCenterOperationStatus.PLANNED.toString():
                color = 'rgb(72,118,251)';
                break;
              case GenDataCenterOperationStatus.LAND_HELD.toString():
                color = 'rgb(187,55,252)';
                break;
              case GenDataCenterOperationStatus.CANCELED.toString():
                color = 'rgb(153,153,153)'; // neutral gray for inactive sites
                break;
            }


            return new Style({
              image: new Circle({
                fill: new Fill({
                  color,
                }),
                stroke: new Stroke({
                  color: 'black',
                  width: 2,
                }),
                radius: 4,
              }),
            });

          },


        });
      }

      MapService.getMapRef()!.addLayer(this.mapLayer().renderedLayer);
      this.changeOpacity(this.opacity);

    } else {
      MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);
    }
  }

  override changeOpacity(value: number) {
    this.opacity = value;
    (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).setOpacity(value / 100);
  }

  override onDestroy(): void {
  }

}
